import { useState, useRef, useEffect, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Mic, MicOff, Square, Volume2, VolumeX, RotateCcw, BookOpen, User } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";

// ─── Types ────────────────────────────────────────────────────────────────────

type VoiceState = "idle" | "starting" | "listening" | "processing" | "speaking" | "error";

interface Message {
  role: "user" | "assistant";
  content: string;
}

interface Props {
  skillNodeId: string;
  courseId?: string;
  nodeTitle: string;
}

// ─── Design tokens (match TutorView) ─────────────────────────────────────────

const GREEN_DEEP = "#1C3829";
const LIME = "hsl(65,65%,75%)";

// ─── Language detection ───────────────────────────────────────────────────────

function detectContentLanguage(text: string): string {
  const sample = text.slice(0, 800).toLowerCase();
  const scores: Record<string, number> = { es: 0, fr: 0, de: 0, pt: 0, it: 0 };

  // Character-level signals (strong indicators)
  if (/[ñ¿¡]/.test(sample)) scores.es += 8;
  if (/ß/.test(sample))      scores.de += 10;
  if (/[àâçœæ]/.test(sample)) scores.fr += 4;
  if (/[ãõ]/.test(sample))   scores.pt += 6;

  // High-frequency stopword scoring (each match = +1.5)
  const stopwords: Record<string, RegExp> = {
    es: /\b(el|la|los|las|un|una|de|que|en|con|por|para|se|es|son|del|al|lo|le|su|sus|más|como|pero|este|esta|estos|estas|tiene|tienen|hay|también|cuando|sobre|entre|hasta|desde|donde|porque|aunque|mientras|durante|después|antes|así|todo|toda|todos|muy)\b/g,
    fr: /\b(le|la|les|un|une|des|et|est|pas|que|ne|en|du|au|aux|se|ce|qui|sur|dans|avec|plus|par|pour|ou|mais|très|bien|aussi|tout|cette|ces|son|sa|ses|ils|elles|nous|vous|leur|même|entre|après|avant|encore|déjà|toujours|peut|être|avoir|faire|dire|voir)\b/g,
    de: /\b(der|die|das|ist|und|nicht|in|von|auf|mit|zu|dem|den|ein|eine|auch|ich|sie|er|wir|haben|sein|werden|aber|oder|wenn|als|wie|nach|vor|über|unter|durch|gegen|ohne|um|bei|seit|wegen|für|an|aus|bis|man|mehr|noch|nur|so|was|wer|wo|zum|zur|vom)\b/g,
    pt: /\b(o|a|os|as|um|uma|de|que|em|com|por|para|se|é|são|do|da|dos|das|ao|no|na|nos|nas|mais|como|mas|este|esta|estes|estas|tem|têm|há|também|quando|sobre|entre|até|desde|onde|porque|muito|bem|fazer|pode|podem|isso|aqui)\b/g,
    it: /\b(il|la|i|le|un|una|di|che|in|con|per|si|è|sono|del|della|dei|delle|al|alla|lo|li|mi|ti|ci|vi|ne|più|come|ma|questo|questa|questi|queste|ha|hanno|c'è|ci|sono|anche|quando|dopo|prima|molto|bene|fare|può|possono)\b/g,
  };
  for (const [lang, re] of Object.entries(stopwords)) {
    scores[lang] += ((sample.match(re) || []).length) * 1.5;
  }

  const [topLang, topScore] = Object.entries(scores).sort((a, b) => b[1] - a[1])[0];
  if (topScore < 4) return "en-US";

  const langMap: Record<string, string> = {
    es: "es-ES", fr: "fr-FR", de: "de-DE", pt: "pt-BR", it: "it-IT",
  };
  return langMap[topLang] ?? "en-US";
}

// ─── Voice selection ─────────────────────────────────────────────────────────

function getBestVoice(langCode = "en-US"): SpeechSynthesisVoice | null {
  const voices = window.speechSynthesis?.getVoices() ?? [];
  const langBase = langCode.split("-")[0].toLowerCase();

  if (langBase === "en") {
    const priority = [
      "Google UK English Female",
      "Samantha",
      "Ava",
      "Allison",
      "Karen",
      "Moira",
      "Google US English",
      "Microsoft Aria Online (Natural)",
      "Microsoft Aria",
    ];
    for (const name of priority) {
      const v = voices.find((v) => v.name === name);
      if (v) return v;
    }
    return (
      voices.find((v) => v.lang === "en-US" && v.name.toLowerCase().includes("female")) ??
      voices.find((v) => v.lang === "en-US") ??
      voices.find((v) => v.lang.startsWith("en-")) ??
      voices[0] ?? null
    );
  }

  // For non-English: prefer natural/neural voices in the right language family
  const pool = voices.filter(
    (v) => v.lang.toLowerCase().startsWith(langBase + "-") || v.lang.toLowerCase() === langBase,
  );
  if (pool.length === 0) return voices[0] ?? null;

  return (
    pool.find((v) => /natural|neural|online/i.test(v.name)) ??
    pool.find((v) => /female|mujer|femme|frau|donna/i.test(v.name)) ??
    pool[0]
  );
}

// ─── Component ────────────────────────────────────────────────────────────────

export default function VoiceTutorView({ skillNodeId, courseId, nodeTitle }: Props) {
  const [voiceState, setVoiceState]       = useState<VoiceState>("idle");
  const [messages, setMessages]           = useState<Message[]>([]);
  const [liveTranscript, setLiveTranscript] = useState("");
  const [errorMsg, setErrorMsg]           = useState<string | null>(null);
  const [isMuted, setIsMuted]             = useState(false);
  const [isSupported, setIsSupported]     = useState<boolean | null>(null);
  const [langCode, setLangCode]           = useState("en-US");

  // Refs so callbacks stay stable and always see current values
  const voiceStateRef  = useRef<VoiceState>("idle");
  const messagesRef    = useRef<Message[]>([]);
  const isMutedRef     = useRef(false);
  const langCodeRef    = useRef("en-US");
  const recognitionRef = useRef<any>(null);
  const processRef     = useRef<(text: string) => void>(() => {});
  const bottomRef      = useRef<HTMLDivElement>(null);

  // Keep refs in sync
  useEffect(() => { voiceStateRef.current = voiceState; }, [voiceState]);
  useEffect(() => { messagesRef.current = messages;     }, [messages]);
  useEffect(() => { isMutedRef.current = isMuted;       }, [isMuted]);
  useEffect(() => { langCodeRef.current = langCode;     }, [langCode]);

  // Browser support check + preload voices
  useEffect(() => {
    const SR = window.SpeechRecognition || (window as any).webkitSpeechRecognition;
    setIsSupported(!!SR);
    if (window.speechSynthesis) {
      window.speechSynthesis.getVoices();
      window.speechSynthesis.addEventListener("voiceschanged", () => window.speechSynthesis.getVoices());
    }
  }, []);

  // Detect content language from lesson content
  useEffect(() => {
    if (!skillNodeId) return;
    let cancelled = false;
    supabase
      .from("skill_node_content")
      .select("content")
      .eq("skill_node_id", skillNodeId)
      .maybeSingle()
      .then(({ data }) => {
        if (cancelled || !data?.content) return;
        const detected = detectContentLanguage(data.content);
        setLangCode(detected);
      });
    return () => { cancelled = true; };
  }, [skillNodeId]);

  // Scroll to bottom on new messages
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      try { recognitionRef.current?.stop(); } catch {}
      window.speechSynthesis?.cancel();
    };
  }, []);

  // ─── speak ────────────────────────────────────────────────────────────────

  const speak = useCallback((text: string, onEnd: () => void) => {
    const synth = window.speechSynthesis;
    if (!synth) { onEnd(); return; }
    synth.cancel();

    if (isMutedRef.current) { onEnd(); return; }

    // Strip markdown for TTS (remove ** __ `` etc.)
    const plain = text
      .replace(/```[\s\S]*?```/g, "")
      .replace(/`([^`]+)`/g, "$1")
      .replace(/\*\*(.+?)\*\*/g, "$1")
      .replace(/\*(.+?)\*/g, "$1")
      .replace(/__(.+?)__/g, "$1")
      .replace(/^#{1,6}\s+/gm, "")
      .replace(/\[(.+?)\]\(.+?\)/g, "$1")
      .replace(/^\s*[-*+]\s+/gm, "")
      .trim();

    const utterance = new SpeechSynthesisUtterance(plain);
    utterance.rate = 1.0;
    utterance.pitch = 1.05;
    utterance.volume = 1.0;
    utterance.lang = langCodeRef.current;
    const voice = getBestVoice(langCodeRef.current);
    if (voice) utterance.voice = voice;
    utterance.onend = onEnd;
    utterance.onerror = onEnd;
    synth.speak(utterance);
  }, []);

  // ─── callTutor ────────────────────────────────────────────────────────────

  const callTutor = useCallback(async (msgs: Message[], isOpening = false): Promise<string> => {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) throw new Error("Not authenticated");
    const { data, error } = await supabase.functions.invoke("course-assistant", {
      body: {
        messages: msgs,
        context: {
          courseId: courseId ?? null,
          courseName: null,
          nodeId: skillNodeId,
          nodeName: nodeTitle,
          pageType: "learner",
          language: langCodeRef.current,
        },
        mode: "tutor",
        generateOpening: isOpening,
        _authToken: session.access_token,
      },
    });
    if (error) throw error;
    return (data?.message as string) || "";
  }, [skillNodeId, courseId, nodeTitle]);

  // ─── startListening ───────────────────────────────────────────────────────

  const startListening = useCallback(() => {
    const SR = window.SpeechRecognition || (window as any).webkitSpeechRecognition;
    if (!SR) return;

    // Stop any existing instance
    try { recognitionRef.current?.stop(); } catch {}

    const rec = new SR();
    rec.continuous = false;
    rec.interimResults = true;
    rec.lang = langCodeRef.current;
    recognitionRef.current = rec;

    rec.onresult = (event: any) => {
      let interim = "";
      let final = "";
      for (let i = event.resultIndex; i < event.results.length; i++) {
        const t = event.results[i][0].transcript;
        if (event.results[i].isFinal) final += t;
        else interim += t;
      }
      setLiveTranscript(final || interim);
      if (final.trim()) {
        setLiveTranscript("");
        processRef.current(final.trim());
      }
    };

    rec.onerror = (e: any) => {
      if (e.error === "not-allowed") {
        setErrorMsg("Microphone access was denied. Please allow microphone in your browser settings and refresh.");
        setVoiceState("error");
        return;
      }
      if (e.error === "aborted") return;
      // no-speech or network — restart quietly
      if (voiceStateRef.current === "listening") {
        setTimeout(() => { if (voiceStateRef.current === "listening") startListening(); }, 400);
      }
    };

    rec.onend = () => {
      if (voiceStateRef.current === "listening") {
        setTimeout(() => { if (voiceStateRef.current === "listening") startListening(); }, 200);
      }
    };

    setVoiceState("listening");
    try { rec.start(); } catch {}
  }, []);

  // ─── processUserSpeech ────────────────────────────────────────────────────

  const processUserSpeech = useCallback(async (text: string) => {
    setVoiceState("processing");
    setLiveTranscript("");
    const updated: Message[] = [...messagesRef.current, { role: "user", content: text }];
    setMessages(updated);

    try {
      const reply = await callTutor(updated);
      setMessages((prev) => [...prev, { role: "assistant", content: reply }]);
      setVoiceState("speaking");
      speak(reply, () => {
        if (voiceStateRef.current === "speaking") startListening();
      });
    } catch {
      setMessages((prev) => [...prev, {
        role: "assistant",
        content: "Sorry, I ran into a problem. Please try again.",
      }]);
      setVoiceState("listening");
      startListening();
    }
  }, [callTutor, speak, startListening]);

  // Keep processRef in sync
  useEffect(() => { processRef.current = processUserSpeech; }, [processUserSpeech]);

  // ─── startSession ─────────────────────────────────────────────────────────

  const startSession = useCallback(async () => {
    setVoiceState("starting");
    setMessages([]);
    setLiveTranscript("");
    setErrorMsg(null);
    try {
      const reply = await callTutor([], true);
      setMessages([{ role: "assistant", content: reply }]);
      setVoiceState("speaking");
      speak(reply, () => {
        if (voiceStateRef.current === "speaking") startListening();
      });
    } catch {
      setErrorMsg("Couldn't start the session. Please try again.");
      setVoiceState("error");
    }
  }, [callTutor, speak, startListening]);

  // ─── stopSession ──────────────────────────────────────────────────────────

  const stopSession = useCallback(() => {
    try { recognitionRef.current?.stop(); } catch {}
    recognitionRef.current = null;
    window.speechSynthesis?.cancel();
    setVoiceState("idle");
    setLiveTranscript("");
  }, []);

  const toggleMute = () => {
    const next = !isMuted;
    setIsMuted(next);
    if (next) window.speechSynthesis?.cancel();
  };

  // ─── Derived ──────────────────────────────────────────────────────────────

  const isActive = voiceState !== "idle" && voiceState !== "error";

  const stateLabel: Record<VoiceState, string> = {
    idle:       "Ready to start",
    starting:   "Preparing your tutor…",
    listening:  "Listening…",
    processing: "Thinking…",
    speaking:   "Speaking…",
    error:      "Something went wrong",
  };

  // ─── Render ───────────────────────────────────────────────────────────────

  if (isSupported === false) {
    return (
      <div style={{ maxWidth: 600, margin: "60px auto", padding: "0 16px", textAlign: "center" }}>
        <div style={{
          width: 72, height: 72, borderRadius: 36, background: GREEN_DEEP,
          display: "flex", alignItems: "center", justifyContent: "center",
          margin: "0 auto 20px",
        }}>
          <MicOff style={{ width: 30, height: 30, color: LIME }} />
        </div>
        <h2 style={{ fontWeight: 700, fontSize: 20, marginBottom: 8 }}>Voice not supported</h2>
        <p style={{ color: "hsl(var(--muted-foreground))", lineHeight: 1.6, marginBottom: 20 }}>
          Your browser doesn't support voice recognition. Please use Chrome or Edge for the voice tutor, or switch to the <strong>AI Tutor</strong> tab for a text-based session.
        </p>
        <Badge variant="outline">Chrome or Edge recommended</Badge>
      </div>
    );
  }

  return (
    <>
      <style>{`
        @keyframes voice-ring {
          0%   { transform: scale(1);   opacity: 0.6; }
          100% { transform: scale(1.9); opacity: 0; }
        }
        @keyframes voice-bar {
          0%, 100% { transform: scaleY(0.3); }
          50%       { transform: scaleY(1); }
        }
        @keyframes voice-breathe {
          0%, 100% { transform: scale(1);    opacity: 1; }
          50%       { transform: scale(1.08); opacity: 0.85; }
        }
        .voice-ring { animation: voice-ring 1.8s ease-out infinite; }
        .voice-ring-2 { animation: voice-ring 1.8s ease-out 0.6s infinite; }
        .voice-ring-3 { animation: voice-ring 1.8s ease-out 1.2s infinite; }
        .voice-bar { animation: voice-bar 0.8s ease-in-out infinite; transform-origin: bottom; }
        .voice-breathe { animation: voice-breathe 2.2s ease-in-out infinite; }
      `}</style>

      <div style={{
        display: "flex", flexDirection: "column",
        height: "calc(100vh - 104px)",
        maxWidth: 720, margin: "0 auto",
      }}>

        {/* ── Central orb area ── */}
        <div style={{
          display: "flex", flexDirection: "column", alignItems: "center",
          justifyContent: "center", padding: "32px 16px 24px", gap: 20,
          flexShrink: 0,
        }}>

          {/* Orb */}
          <div style={{ position: "relative", width: 140, height: 140, display: "flex", alignItems: "center", justifyContent: "center" }}>

            {/* Listening rings */}
            {voiceState === "listening" && (
              <>
                <div className="voice-ring" style={{ position: "absolute", inset: 0, borderRadius: "50%", border: `2px solid ${GREEN_DEEP}`, opacity: 0 }} />
                <div className="voice-ring-2" style={{ position: "absolute", inset: 0, borderRadius: "50%", border: `2px solid ${GREEN_DEEP}`, opacity: 0 }} />
                <div className="voice-ring-3" style={{ position: "absolute", inset: 0, borderRadius: "50%", border: `2px solid ${GREEN_DEEP}`, opacity: 0 }} />
              </>
            )}

            {/* Speaking wave bars */}
            {voiceState === "speaking" && (
              <div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", gap: 5 }}>
                {[0, 0.12, 0.24, 0.36, 0.12, 0, 0.24].map((delay, i) => (
                  <div
                    key={i}
                    className="voice-bar"
                    style={{
                      width: 4, height: i === 3 ? 48 : i % 2 === 0 ? 32 : 40,
                      borderRadius: 2,
                      background: `${GREEN_DEEP}44`,
                      animationDelay: `${delay}s`,
                    }}
                  />
                ))}
              </div>
            )}

            {/* Core circle */}
            <div
              className={voiceState === "processing" ? "voice-breathe" : ""}
              style={{
                width: 96, height: 96, borderRadius: 48,
                background: GREEN_DEEP,
                display: "flex", alignItems: "center", justifyContent: "center",
                boxShadow: voiceState === "listening"
                  ? `0 0 0 6px ${GREEN_DEEP}22, 0 0 0 12px ${GREEN_DEEP}11`
                  : voiceState === "speaking"
                  ? `0 0 24px ${GREEN_DEEP}44`
                  : "0 4px 16px rgba(28,56,41,0.22)",
                transition: "box-shadow 0.4s",
              }}
            >
              {voiceState === "listening" ? (
                <Mic style={{ width: 34, height: 34, color: LIME }} />
              ) : voiceState === "speaking" ? (
                <Volume2 style={{ width: 34, height: 34, color: LIME }} />
              ) : (
                <BookOpen style={{ width: 34, height: 34, color: LIME }} />
              )}
            </div>
          </div>

          {/* State label */}
          <p style={{
            fontSize: 15, fontWeight: 600,
            color: voiceState === "error" ? "hsl(var(--destructive))" : "hsl(var(--foreground))",
          }}>
            {stateLabel[voiceState]}
          </p>

          {/* Live transcript preview */}
          {liveTranscript && (
            <p style={{
              fontSize: 13, color: "hsl(var(--muted-foreground))",
              fontStyle: "italic", maxWidth: 360, textAlign: "center",
              padding: "6px 12px", background: "hsl(var(--muted))",
              borderRadius: 20, lineHeight: 1.5,
            }}>
              "{liveTranscript}"
            </p>
          )}

          {/* Error message */}
          {voiceState === "error" && errorMsg && (
            <p style={{
              fontSize: 13, color: "hsl(var(--muted-foreground))",
              maxWidth: 360, textAlign: "center", lineHeight: 1.6,
              padding: "8px 16px", background: "hsl(var(--muted))", borderRadius: 12,
            }}>
              {errorMsg}
            </p>
          )}

          {/* Controls */}
          <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
            {!isActive ? (
              <Button
                onClick={startSession}
                disabled={voiceState === "starting"}
                style={{ background: GREEN_DEEP, color: LIME, gap: 6, padding: "0 20px", height: 40 }}
              >
                <Mic style={{ width: 16, height: 16 }} />
                Start voice session
              </Button>
            ) : (
              <>
                <Button
                  variant="outline"
                  size="icon"
                  onClick={toggleMute}
                  title={isMuted ? "Unmute" : "Mute tutor voice"}
                  style={{ height: 40, width: 40 }}
                >
                  {isMuted
                    ? <VolumeX style={{ width: 16, height: 16 }} />
                    : <Volume2 style={{ width: 16, height: 16 }} />
                  }
                </Button>
                <Button
                  variant="destructive"
                  onClick={stopSession}
                  style={{ gap: 6, padding: "0 16px", height: 40 }}
                  title="End session"
                >
                  <Square style={{ width: 14, height: 14 }} />
                  End session
                </Button>
              </>
            )}

            {messages.length > 0 && !isActive && (
              <Button
                variant="ghost"
                size="sm"
                onClick={startSession}
                style={{ gap: 4, height: 40 }}
                title="Start a new session"
              >
                <RotateCcw style={{ width: 13, height: 13 }} />
                New session
              </Button>
            )}
          </div>

          {isActive && voiceState === "listening" && (
            <p style={{ fontSize: 11, color: "hsl(var(--muted-foreground))", marginTop: -8 }}>
              Speak now — I'll respond when you pause
            </p>
          )}

          {!isActive && voiceState === "idle" && (
            <p style={{ fontSize: 12, color: "hsl(var(--muted-foreground))", marginTop: -8, textAlign: "center", maxWidth: 320, lineHeight: 1.6 }}>
              Your AI tutor will ask Socratic questions about <strong>{nodeTitle}</strong> and respond to yours — hands-free.
            </p>
          )}

          {langCode !== "en-US" && (
            <Badge variant="secondary" style={{ fontSize: 11 }}>
              {new Intl.DisplayNames(["en"], { type: "language" }).of(langCode.split("-")[0])} detected
            </Badge>
          )}
        </div>

        {/* ── Transcript ── */}
        {messages.length > 0 && (
          <>
            <div style={{ height: 1, background: "hsl(var(--border))", flexShrink: 0 }} />
            <ScrollArea style={{ flex: 1 }}>
              <div style={{ padding: "16px 16px 24px" }}>
                <p style={{ fontSize: 11, fontWeight: 600, letterSpacing: "0.08em", textTransform: "uppercase", color: "hsl(var(--muted-foreground))", marginBottom: 14 }}>
                  Transcript
                </p>
                <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
                  {messages.map((msg, i) => (
                    <div
                      key={i}
                      style={{
                        display: "flex",
                        justifyContent: msg.role === "user" ? "flex-end" : "flex-start",
                        gap: 8, alignItems: "flex-start",
                      }}
                    >
                      {msg.role === "assistant" && (
                        <div style={{
                          width: 30, height: 30, borderRadius: 15, flexShrink: 0,
                          background: GREEN_DEEP,
                          display: "flex", alignItems: "center", justifyContent: "center",
                          marginTop: 2,
                        }}>
                          <BookOpen style={{ width: 13, height: 13, color: LIME }} />
                        </div>
                      )}
                      <div style={{
                        maxWidth: "76%",
                        background: msg.role === "user" ? GREEN_DEEP : "hsl(var(--muted))",
                        color: msg.role === "user" ? "#EAE6DC" : "hsl(var(--foreground))",
                        borderRadius: msg.role === "user" ? "16px 16px 4px 16px" : "4px 16px 16px 16px",
                        padding: "10px 14px",
                        fontSize: 13,
                        lineHeight: 1.65,
                      }}>
                        <div className="prose prose-sm dark:prose-invert max-w-none">
                          <ReactMarkdown remarkPlugins={[remarkGfm]}>{msg.content}</ReactMarkdown>
                        </div>
                      </div>
                      {msg.role === "user" && (
                        <div style={{
                          width: 30, height: 30, borderRadius: 15, flexShrink: 0,
                          background: "hsl(var(--muted))",
                          display: "flex", alignItems: "center", justifyContent: "center",
                          marginTop: 2,
                        }}>
                          <User style={{ width: 13, height: 13 }} />
                        </div>
                      )}
                    </div>
                  ))}
                </div>
                <div ref={bottomRef} />
              </div>
            </ScrollArea>
          </>
        )}
      </div>
    </>
  );
}
